
import React from 'react'
import {
  Box,
  Chip,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
} from '@mui/material'

type Item = { id: string; label: string; done?: boolean }
type Props = {
  items?: Item[]
  editing?: boolean
  onToggle?: (item: Item) => void
  height?: string
  dense?: boolean
  title?: string
  /** optional: render extra UI under the row (notes, metadata, etc.) */
  renderExtra?: (item: Item) => React.ReactNode
}

export default function TaskChecklistTable({
  items = [],
  editing = false,
  onToggle,
  height = 'calc(80vh - 220px)',
  dense = true,
  title = 'Checklist',
  renderExtra,
}: Props) {
  const done = items.filter(i => i?.done).length
  const total = items.length
  const pct = total ? Math.round((done / total) * 100) : 0
  const rowSize = dense ? 'small' : 'medium'

  return (
    <Box>
      {/* Header / progress */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1 }}>
        <Typography variant="body2">{title}</Typography>
        <Chip size="small" label={`${done}/${total}`} />
        <Chip
          size="small"
          label={editing ? 'Draft' : 'Read-only'}
          color={editing ? 'warning' : 'default'}
          variant={editing ? 'filled' : 'outlined'}
        />
        <Box sx={{ flex: 1, mx: 1 }}>
          <LinearProgress variant="determinate" value={pct} />
        </Box>
        <Typography variant="body2">{pct}%</Typography>
      </Box>

      <TableContainer sx={{ height: height ?? '100%', maxHeight: '100vh', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)' }}>
        <Table
          size={rowSize}
          stickyHeader
          sx={{
            '& .MuiTableCell-root': { textAlign: 'left' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell width={120} align="right">
                Done
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {total === 0 ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    No checklist for this task.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map(item => (
                <React.Fragment key={item.id}>
                  {/* Main row: single cell spanning both columns, using grid for left/right alignment */}
                  <TableRow
                    hover={editing}
                    sx={{ cursor: editing ? 'pointer' : 'default', bgcolor: 'background.defaultdim' }}
                  >
                    <TableCell colSpan={2} sx={{ py: dense ? 0 : 1 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          alignItems: 'center',
                          columnGap: 1.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ textDecoration: item.done ? 'line-through' : 'none' }}>
                          {item.label}
                        </Typography>

                        <Tooltip title={editing ? '' : 'Click "Edit Checklist" to modify'}>
                          <span>
                            <Checkbox
                              size="small"
                              checked={!!item.done}
                              disabled={!editing}
                              onChange={editing ? () => onToggle?.(item) : undefined}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  color: editing ? 'text.primary' : 'text.disabled',
                                },
                              }}
                            />
                          </span>
                        </Tooltip>
                      </Box>

                      {/* Optional extra content below the row (notes, fields, etc.) */}
                      {renderExtra && <Box sx={{ mt: 0.75 }}>{renderExtra(item)}</Box>}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
